import { Injectable } from '@angular/core';
import { injectProduction } from '@y42/environment';
import { firstValueFrom, of } from 'rxjs';
import { UserTrackingPreferencesServiceLegacy } from './provide-user-tracking';
import { SegmentPageEvent, SegmentService, SegmentTrackingEvent } from './segment.service';
import {
  GroupTrackingEvent,
  IdentificationTrackingEvent,
  PageTrackingEvent,
  TrackingEvent,
} from './user-tracking.model';

@Injectable({ providedIn: 'root' })
export class UserTrackingLegacyService {
  constructor(
    private readonly segment: SegmentService,
    private readonly userTrackingPreferencesServiceLegacy: UserTrackingPreferencesServiceLegacy,
  ) {}

  private readonly production = injectProduction();

  async identify(event: IdentificationTrackingEvent) {
    if (!this.canCall()) {
      return;
    }

    return await firstValueFrom(this.segment.identify(event) || of(false));
  }

  async track(event: TrackingEvent) {
    if (!this.canCall()) {
      return;
    }

    const properties = Object.entries(event.properties || {}).map(([key, value]) => ({ key, value }));
    const data: SegmentTrackingEvent = {
      event: event.name,
      properties: [...this.getBaseProperties(), ...properties],
      context: { ...this.getBaseContext(), ...(event.context || {}) },
    };

    return await this.segment.track(data);
  }

  async trackGroup(event: GroupTrackingEvent) {
    if (!this.canCall()) {
      return;
    }

    return await this.segment.createGroup(event);
  }

  async trackPage(event: PageTrackingEvent) {
    if (!this.canCall()) {
      return;
    }

    const properties = [{ key: 'url', value: event.url }];

    const data: SegmentPageEvent = {
      type: 'page',
      title: event.name,
      context: this.getBaseContext(),
      url: event.url,
      properties: [...this.getBaseProperties(), ...properties],
    };

    return await this.segment.page(data);
  }

  private getBaseProperties(): Array<{ key: string; value: string }> {
    return this.userTrackingPreferencesServiceLegacy.getBaseProperties();
  }

  private getBaseContext() {
    return this.userTrackingPreferencesServiceLegacy.getBaseContext();
  }

  private canCall() {
    return (
      location.host.indexOf('localhost') === -1 &&
      !!this.userTrackingPreferencesServiceLegacy.getUser() &&
      this.production
    );
  }
}
